import { getCreatedTimeMsFromCreated } from "./dateUtils";
import { isTapeReading } from "./tradeHelpers";

export function isCapitalTrade(entry) {
  return !entry.isDeposit && !entry.isPayout && !isTapeReading(entry) && !entry.isResetExcluded &&
    entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== "";
}

const dayKey = (year, month, day) => `${parseInt(year, 10)}-${parseInt(month, 10)}-${parseInt(day, 10)}`;

// Returns (year, month, day) => account capital right before that day's first trade.
// Trades can sort ahead of the funding entries (CSV imports are stamped with the
// trade date, the initial deposit with the account creation time) and carry no
// accountBalance, so fall back to the first trade's inferred balance, then net
// funding, then the account's initial balance.
export function buildDayCapitalLookup(entries, account = null) {
  const sortedEntries = [...(entries || [])].sort(
    (a, b) => getCreatedTimeMsFromCreated(a.created) - getCreatedTimeMsFromCreated(b.created)
  );

  const capitalBeforeFirstTradeByDay = {};
  let runningCapital = 0;
  let netFunding = 0;
  sortedEntries.forEach((entry) => {
    const isTrade = isCapitalTrade(entry);
    const key = dayKey(entry.year, entry.month, entry.day);
    if (isTrade && capitalBeforeFirstTradeByDay[key] === undefined) {
      capitalBeforeFirstTradeByDay[key] = runningCapital;
    }

    if (entry.isBalanceAdjustment) {
      // Balance adjustments set the balance to their target (same rule as accountBalanceUtils).
      const target = Number(entry.adjustmentTarget ?? entry.accountBalance);
      runningCapital = Number.isFinite(target) ? target : runningCapital + (Number(entry.pnl) || 0);
    } else if (entry.isDeposit) {
      runningCapital += Number(entry.pnl) || 0;
      netFunding += Number(entry.pnl) || 0;
    } else if (entry.isPayout) {
      runningCapital -= Math.abs(Number(entry.pnl) || 0);
      netFunding -= Math.abs(Number(entry.pnl) || 0);
    } else if (isTrade) {
      runningCapital += Number(entry.pnl) || 0;
    }
  });

  const firstTrade = sortedEntries.find(e => !e.isDeposit && !e.isPayout && !isTapeReading(e) && !e.isResetExcluded);
  let inferredStartingCapital = 0;
  if (firstTrade && firstTrade.accountBalance) {
    inferredStartingCapital = (Number(firstTrade.accountBalance) || 0) - (Number(firstTrade.pnl) || 0);
  }
  const accountInitialBalance = Number(account?.initialBalance);
  const fallbackCapital = inferredStartingCapital > 0
    ? inferredStartingCapital
    : netFunding > 0
      ? netFunding
      : (Number.isFinite(accountInitialBalance) && accountInitialBalance > 0 ? accountInitialBalance : 0);

  return (year, month, day) => {
    const dayCapital = capitalBeforeFirstTradeByDay[dayKey(year, month, day)];
    return dayCapital > 0 ? dayCapital : fallbackCapital;
  };
}

// 999999 marks a profitable day on an account with no known capital ("New Account").
export function getDayPercentage(pnl, capital) {
  if (capital > 0) return (pnl / capital) * 100;
  return pnl > 0 ? 999999 : 0;
}
